import { useProfile } from "@/hooks/useProfile";
import MainLayout from "@/components/layout/MainLayout";
import { useState } from "react";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import {
  useTransactions,
  WithdrawalStatus,
  PaymentStatus,
} from "@/hooks/useTransactions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MinutePackageCard from "@/components/balance/MinutePackageCard";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/contexts/NotificationContext";
import TransactionHistory from "@/components/balance/TransactionHistory";
import { Pagination } from "@/components/ui/pagination";
import {
  useMinutePackages,
  FormattedMinutePackage,
} from "@/hooks/useMinutePackages";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  initiatePackagePayment,
  usePaymentPage,
} from "@/integrations/payments/paymentService";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Zap, Mail, History, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import CurrencySelector from "@/components/balance/CurrencySelector";
import { getCurrencySymbol } from "@/lib/currency";

const TRANSACTIONS_PER_PAGE = 10;

const Balance = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [currentPage, setCurrentPage] = useState(1);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<FormattedMinutePackage | null>(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [emailError, setEmailError] = useState("");
  const { openPayment, preparePaymentWindow } = usePaymentPage();

  const {
    transactions,
    totalCount,
    isLoading: isLoadingTransactions,
    error: transactionsError,
  } = useTransactions({
    userId: user?.id,
    limit: TRANSACTIONS_PER_PAGE,
    offset: (currentPage - 1) * TRANSACTIONS_PER_PAGE,
    orderBy: { column: "created_at", ascending: false },
  });

  const { profile } = useProfile(user?.id);

  const {
    data: minutePackages,
    isLoading: isLoadingPackages,
    error: packagesError,
  } = useMinutePackages({ currency: profile?.currency || 'RUB' });

  const basePricePerMinute = minutePackages && minutePackages.length > 0
    ? Number(minutePackages[0].price) / minutePackages[0].minutesAmount
    : undefined;

  const handlePackageSelect = (packageData: FormattedMinutePackage) => {
    setSelectedPackage(packageData);
    setIsPaymentDialogOpen(true);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirmPayment = async () => {
    if (!selectedPackage) return;

    // Валидация email
    if (!email.trim()) {
      setEmailError(t('balance.validation.email_required'));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t('balance.validation.email_invalid'));
      return;
    }

    setEmailError("");

    // Резервируем вкладку синхронно, пока действует жест пользователя —
    // после await Safari на iOS заблокирует window.open
    const paymentWindow = preparePaymentWindow();

    setIsProcessingPayment(true);
    setIsPaymentDialogOpen(false);

    try {
      // Выводим ID пакета в консоль
      console.log("Выбран пакет с ID:", selectedPackage.id);

      // Инициируем платеж и получаем URL для оплаты
      const confirmationUrl = await initiatePackagePayment(
        selectedPackage.id,
        email
      );

      // Открываем страницу оплаты в новом окне
      openPayment(confirmationUrl, paymentWindow);
    } catch (error) {
      paymentWindow?.close();
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('balance.payment.error_init'),
      });
      console.error("Ошибка платежа:", error);
    } finally {
      setIsProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  const handleCancelPayment = () => {
    setIsPaymentDialogOpen(false);
    setSelectedPackage(null);
    setEmailError("");
  };

  return (
    <MainLayout>
      <div className="container mx-auto max-w-5xl px-4 py-8">
        {/* Header / Current Balance Hero */}
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('balance.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('balance.subtitle')}
            </p>
          </div>
        </div>

        <Tabs defaultValue="buy" className="space-y-8">
          <TabsList className="w-full grid grid-cols-1 h-auto sm:inline-flex sm:w-auto sm:h-11 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
            <TabsTrigger
              value="buy"
              className="flex items-center justify-center gap-2 px-6 py-2 sm:py-1.5 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <CreditCard size={16} />
              {t('balance.tabs.buy')}
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="flex items-center justify-center gap-2 px-6 py-2 sm:py-1.5 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <History size={16} />
              {t('balance.tabs.history')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buy" className="space-y-12 focus-visible:outline-none">
            <div>
              <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="text-center md:text-left">
                  <h2 className="text-2xl font-semibold tracking-tight">{t('balance.package_selection.title')}</h2>
                  <p className="text-muted-foreground mt-2">
                    {t('balance.package_selection.subtitle')}
                  </p>
                </div>
                <CurrencySelector />
              </div>

              {isLoadingPackages ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-[380px] border rounded-2xl bg-card p-6 animate-pulse"
                    >
                      <div className="h-6 w-1/2 bg-muted rounded mb-4"></div>
                      <div className="h-10 w-3/4 bg-muted rounded mb-4"></div>
                      <div className="h-4 w-full bg-muted rounded mb-2"></div>
                      <div className="h-4 w-2/3 bg-muted rounded mb-8"></div>
                      <div className="mt-auto h-12 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : packagesError ? (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center text-destructive">
                  {t('balance.package_selection.error_loading')}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 xl:gap-8">
                  {minutePackages?.map((packageData) => (
                    <MinutePackageCard
                      key={packageData.id}
                      packageData={{
                        id: packageData.id,
                        name: packageData.title || "",
                        minutes: packageData.minutesAmount,
                        price: Number(packageData.price),
                        isPopular: packageData.isPopular || false,
                        currency: packageData.currency || profile?.currency || 'RUB',
                        titleKey: packageData.titleKey,
                      }}
                      basePricePerMinute={basePricePerMinute}
                      onSelect={() => handlePackageSelect(packageData)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Trust Signals Section */}
            <div className="rounded-2xl bg-muted/50 p-6 md:p-8">
              <div className="grid gap-6 md:grid-cols-3 md:gap-8">
                <div className="flex flex-row items-start text-left gap-4 md:flex-col md:gap-0">
                  <div className="shrink-0 rounded-full bg-background p-3 shadow-sm md:mb-3">
                    <ShieldCheck className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('balance.trust.safe_payment.title')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('balance.trust.safe_payment.desc')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-start text-left gap-4 md:flex-col md:gap-0">
                  <div className="shrink-0 rounded-full bg-background p-3 shadow-sm md:mb-3">
                    <Zap className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('balance.trust.instant.title')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('balance.trust.instant.desc')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-start text-left gap-4 md:flex-col md:gap-0">
                  <div className="shrink-0 rounded-full bg-background p-3 shadow-sm md:mb-3">
                    <Mail className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('balance.trust.receipt.title')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('balance.trust.receipt.desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="focus-visible:outline-none">
            <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
              <div className="border-b p-4 sm:p-6">
                <h2 className="text-xl font-semibold">{t('balance.history.title')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('balance.history.subtitle')}
                </p>
              </div>

              <div className="p-4 sm:p-6">
                {isLoadingTransactions ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">
                    {t('balance.history.loading')}
                  </div>
                ) : transactionsError ? (
                  <div className="text-center py-8 text-red-500">
                    {t('balance.history.error_loading')}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <TransactionHistory
                      transactions={transactions}
                      renderStatus={(transaction) => {
                        if (
                          transaction.type === "withdrawal" &&
                          transaction.withdrawal_status
                        ) {
                          const statusKey = `balance.status.${transaction.withdrawal_status}` as const;
                          // @ts-expect-error dynamic status key
                          return t(statusKey) || transaction.withdrawal_status;
                        }

                        if (transaction.type === "deposit" || transaction.type === "bonus") {
                          const statusKey = `balance.status.${transaction.payment_status}` as const;
                          // @ts-expect-error dynamic status key
                          return t(statusKey) || transaction.payment_status;
                        }

                        return "";
                      }}
                    />

                    {totalCount > TRANSACTIONS_PER_PAGE && (
                      <div className="flex justify-center pt-4">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={Math.ceil(
                            totalCount / TRANSACTIONS_PER_PAGE
                          )}
                          totalItems={totalCount}
                          itemsPerPage={TRANSACTIONS_PER_PAGE}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Диалог подтверждения оплаты */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('balance.confirmation.title')}</DialogTitle>
            <DialogDescription>
              {t('balance.confirmation.desc')}
            </DialogDescription>
          </DialogHeader>

          {selectedPackage && (
            <div className="space-y-6">
              <div className="rounded-lg bg-muted p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{selectedPackage.title}</span>
                  <span className="font-bold text-brand-600">{Number(selectedPackage.price)} {getCurrencySymbol(selectedPackage.currency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>{t('balance.confirmation.package_volume')}</span>
                  <span>{selectedPackage.minutesAmount} {t('balance.package.minutes')}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="email">{t('balance.confirmation.email_label')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
                />
                {emailError && (
                  <p className="text-sm text-destructive font-medium">{emailError}</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={handleCancelPayment}>
              {t('balance.confirmation.cancel_btn')}
            </Button>
            <Button
              onClick={handleConfirmPayment}
              disabled={isProcessingPayment}
              className="bg-brand-600 hover:bg-brand-700 text-white"
            >
              {isProcessingPayment ? t('balance.package.processing') : t('balance.confirmation.pay_btn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Balance;
